import {Component, ViewChild, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxDataGridModule,
    DxDateBoxModule,
    DxFormComponent,
    DxFormModule,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import {
    AccessType,
    AuthService,
    Country,
    MustDepartTimeGridViewDto,
    PermissionType,
    Province,
    ShipToTimeZone,
} from '@services';
import {FilterPanelComponent} from '../../../components/library/filter-panel/filter-panel.component';
import {FilterConfig, GridPageComponent} from '../../common/grid-page.component';
import {ToDatePipe} from "../../../pipes/to-date.pipe";

@Component({
    selector: 'app-must-depart-times',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxDataGridModule,
        DxDateBoxModule,
        DxButtonComponent,
        DxTemplateDirective,
        DxiItemComponent,
        DxiColumnComponent,
        DxoHeaderFilterComponent,
        DxoScrollingComponent,
        DxoColumnChooserComponent,
        DxoExportComponent,
        DxoLoadPanelComponent,
        DxoSortingComponent,
        DxoToolbarComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoFilterRowComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        DxFormComponent,
        DxFormModule,
        FilterPanelComponent,
        ToDatePipe,
    ],
    templateUrl: './must-depart-times.component.html',
})
export class MustDepartTimesComponent extends GridPageComponent<MustDepartTimeGridViewDto> {
    @ViewChild(DxDataGridComponent, {static: false}) grid!: DxDataGridComponent;

    private auth = inject(AuthService);

    protected override exportName: string = 'Must Depart Times Report';
    protected offset: number = 100;

    dataSource = this.dataSources.mustDepartTimes();

    canManage = this.auth.checkPermission(PermissionType.MustDepartTimes, AccessType.Manage);

    provinceOptions: { value: Province; text: string }[] = [
        {value: Province.Alberta, text: 'Alberta'},
        {value: Province.BritishColumbia, text: 'British Columbia'},
        {value: Province.Manitoba, text: 'Manitoba'},
        {value: Province.NewBrunswick, text: 'New Brunswick'},
        {value: Province.NewfoundlandAndLabrador, text: 'Newfoundland and Labrador'},
        {value: Province.NorthwestTerritories, text: 'Northwest Territories'},
        {value: Province.NovaScotia, text: 'Nova Scotia'},
        {value: Province.Nunavut, text: 'Nunavut'},
        {value: Province.Ontario, text: 'Ontario'},
        {value: Province.PrinceEdwardIsland, text: 'Prince Edward Island'},
        {value: Province.Quebec, text: 'Quebec'},
        {value: Province.Saskatchewan, text: 'Saskatchewan'},
        {value: Province.Yukon, text: 'Yukon'},
    ];

    countryOptions: { value: Country; text: string }[] = [
        {value: Country.Canada, text: 'Canada'},
    ];

    timeZoneOptions: { value: ShipToTimeZone; text: string; short: string }[] = [
        {value: ShipToTimeZone.NewfoundlandTime, text: 'Newfoundland Time', short: 'NST'},
        {value: ShipToTimeZone.AtlanticTime, text: 'Atlantic Time', short: 'AST'},
        {value: ShipToTimeZone.EasternTime, text: 'Eastern Time', short: 'EST'},
        {value: ShipToTimeZone.CentralTime, text: 'Central Time', short: 'CST'},
        {value: ShipToTimeZone.MountainTime, text: 'Mountain Time', short: 'MST'},
        {value: ShipToTimeZone.PacificTime, text: 'Pacific Time', short: 'PST'},
        {value: ShipToTimeZone.SaskatchewanTime, text: 'Saskatchewan Time', short: 'CST'},
        {value: ShipToTimeZone.YukonTime, text: 'Yukon Time', short: 'MST'},
    ];

    protected override filterConfig: FilterConfig[] = [
        {field: 'city', op: 'contains'},
        {field: 'province', op: '='},
        {field: 'shipToTimeZone', op: '='},
    ];

    constructor() {
        super();
        this.init(MustDepartTimeGridViewDto);
    }

    addNew() {
        this.grid.instance.addRow();
    }

    onInitNewRow = (e: any) => {
        e.data.country = Country.Canada;
    };

    onRowUpdating(e: any) {
        e.newData = Object.assign({}, e.oldData, e.newData);
    }

    onRowDblClick(e: any) {
        if (e.rowType === 'data' && this.canManage) {
            e.component.editRow(e.rowIndex);
        }
    }

    timeZoneShort = (value?: ShipToTimeZone): string =>
        this.timeZoneOptions.find(o => o.value === value)?.short ?? '';

    isWholeNumber = (options: { value?: number }): boolean =>
        options.value == null || Number.isInteger(options.value);
}
